import { z } from 'zod';
import { eventSchema } from './envelope';

export const EMERGENCY_INCIDENT_STARTED_V1 = 'emergency.incident.started.v1' as const;
export const EMERGENCY_INCIDENT_CLOSED_V1 = 'emergency.incident.closed.v1' as const;
export const EMERGENCY_RESPONSE_RECORDED_V1 = 'emergency.response.recorded.v1' as const;

/**
 * Er is een noodmelding gestart.
 *
 * **Met de titel en de instructie**, en dat wijkt bewust af van elk ander event in dit platform.
 * Elders draagt een event nooit inhoud (ADR-0038): een melding zegt dát er iets is, en de tekst
 * haalt de client op achter de aanmelding. Hier niet — "ontruim hal 3" moet op een vergrendeld
 * scherm staan, want een medewerker die eerst de app moet openen om te weten dát hij naar buiten
 * moet, is een medewerker die te laat is.
 *
 * Dat is een afweging en geen vergetelheid: de prijs is dat de instructie leesbaar is voor wie de
 * telefoon in handen heeft, en dat die tekst in het eventspoor blijft staan. Bij een ontruiming
 * weegt dat niet op tegen een seconde winst (ADR-0060).
 *
 * De ontvangers gaan mee in het event, in stukken van `RECIPIENT_CHUNK_SIZE` — hetzelfde patroon
 * als bij een gepubliceerd bericht. Dat moet, want `notifications` schrijft de inboxregels en is
 * **niet** van `emergency` afhankelijk: beide modules zijn optioneel, en een module die een andere
 * rechtstreeks importeert valt om zodra die uit staat (ADR-0021).
 */
export const incidentStartedV1Schema = eventSchema({
  incidentId: z.guid(),
  title: z.string(),
  instruction: z.string(),
  /** Een oefening. `notifications` zet dat in de meldingstekst, zodat niemand schrikt voor niets. */
  isDrill: z.boolean(),
  recipientIds: z.array(z.guid()),
  /** 1-gebaseerd; één stuk betekent `1` van `1`. */
  chunk: z.number().int().min(1),
  chunkCount: z.number().int().min(1),
});
export type IncidentStartedV1 = z.infer<typeof incidentStartedV1Schema>;

/**
 * Het incident is gesloten.
 *
 * Hierop stoppen de herinneringen. Dat gebeurt niet door jobs te verwijderen maar doordat elke
 * herinneringsjob bij het uitvoeren opnieuw kijkt of het incident nog loopt — een geannuleerde job
 * die toch draait, is een melding die niemand kan uitleggen.
 */
export const incidentClosedV1Schema = eventSchema({
  incidentId: z.guid(),
  isDrill: z.boolean(),
  safeCount: z.number().int(),
  helpCount: z.number().int(),
  silentCount: z.number().int(),
});
export type IncidentClosedV1 = z.infer<typeof incidentClosedV1Schema>;

/**
 * Iemand liet weten of hij veilig is.
 *
 * Zonder de nota: die kan "ben bij de poort met Jan" zijn, en dat is persoonlijke informatie die
 * niet in een eventspoor hoort. Wie de nota nodig heeft, leest het overzicht.
 */
export const responseRecordedV1Schema = eventSchema({
  incidentId: z.guid(),
  personId: z.guid(),
  status: z.enum(['safe', 'help']),
});
export type ResponseRecordedV1 = z.infer<typeof responseRecordedV1Schema>;

export const emergencyEventCatalog = {
  [EMERGENCY_INCIDENT_STARTED_V1]: incidentStartedV1Schema,
  [EMERGENCY_INCIDENT_CLOSED_V1]: incidentClosedV1Schema,
  [EMERGENCY_RESPONSE_RECORDED_V1]: responseRecordedV1Schema,
} as const;
